import { useEffect, useRef, useState } from 'react';
import SvgTriangle from 'src/components/Common/Svgs/Triangle';
import track from 'src/lib/Analytics';
import { AccountSelector } from '../../../Common/@electron/AccountSelector';
import { DisplayAddress } from '../../_components/DisplayAddress';
import { selectAccount } from '../_api/accounts';
import { AccountChip } from '../_components/AccountChip';
import { SearchAccounts } from '../_components/SearchAccounts';
import { sendCAL, sendCalNoReturnCode, sendVpv } from '../analytics';
import { AccountSelectionType, AccountType, ReturnCode, ShowAllButtonType } from '../types';

const ShowAllButton = ({ numberOfAccounts, showAll, toggleShowAll }: ShowAllButtonType) => {
  return (
    <button
      className="flex items-center justify-center mx-auto mt-24 text-teal-darker max-w-fit"
      aria-label={showAll ? 'Show fewer accounts' : `Show all ${numberOfAccounts} accounts`}
      onClick={toggleShowAll}
    >
      <p className="mr-8 underline underline-offset-8">
        {showAll ? 'Show fewer' : `Show all ${numberOfAccounts}`}
      </p>
      <SvgTriangle className={`mb-2 ${showAll ? '' : 'rotate-180'}`} height={12} />
    </button>
  );
};

export const AccountSelection = ({
  accountMasking,
  addressMasking,
  accounts,
  prepaidLabel,
  selectedAccount,
  selectAccountEndpoint,
  returnCodes,
}: AccountSelectionType) => {
  const [showAll, setShowAll] = useState(accounts && accounts?.length < 11);
  const evaluateNumberOfAccounts = accounts?.length;
  const firstAccountRef = useRef<HTMLButtonElement>(null);

  const sixthAccountRef = useRef<HTMLButtonElement>(null);

  // Analytics on page load
  useEffect(() => {
    if (accounts) {
      sendVpv('load', evaluateNumberOfAccounts);
    }
  }, []);

  useEffect(() => {
    if (showAll && sixthAccountRef.current) {
      sixthAccountRef?.current?.focus();
    }
    if (!showAll && firstAccountRef.current) {
      firstAccountRef?.current?.focus();
    }
  }, [showAll]);

  // Formatting account info
  const getAccountNumber = (account: AccountType) => {
    const masked = accountMasking;
    if (masked) {
      return `**${account.AccountNum.substring(account.AccountNum.length - 4)}`;
    }

    return account.AccountNum;
  };

  const getMaskedAddress = (account: AccountType) => {
    const address = `${account.Housenum} ${account.Street}`;
    if (address.length <= 8) {
      return address
        .substring(0, Math.ceil(address.length / 2))
        .toLocaleLowerCase()
        .trim();
    }
    return address.substring(0, 8).toLocaleLowerCase().trim();
  };

  const getAccountAriaLabel = (account: AccountType) => {
    return `${
      account.AccountNickname.toLowerCase() || account.CustomerNamePrimary.toLowerCase()
    }, account number ${account.AccountNum.toString().split('').join(' ')}, status, ${
      account.Status
    }, `;
  };

  const getMessageByName = (returnCodes: ReturnCode[], rcId: string) => {
    return (
      (returnCodes.find((returnCode: ReturnCode) => returnCode.name === rcId) || {}).fields?.Message
        ?.value || null
    );
  };

  // Handlers
  const handleToggleShowAll = () => {
    if (!showAll) {
      sendVpv('load', evaluateNumberOfAccounts, 'show-all');
    }
    setShowAll(showAll => !showAll);
  };

  const formRef = useRef<HTMLFormElement>(null);
  const userHasSearched = useRef(false);
  const [accountList, setAccountList] = useState(accounts);
  const [ariaSearchAlertMessage, setAriaSearchAlertMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!userHasSearched.current) {
      sendCAL('account-selector', 'search-by-account-address', 'search');
      userHasSearched.current = true;
    }

    const filteredAccountsList = accounts?.filter(value => {
      return Object.values(value)
        .join(' ')
        .toLocaleLowerCase()
        .includes(e.currentTarget.value.toLocaleLowerCase() || '');
    });

    setAccountList(filteredAccountsList);

    if (!e.currentTarget.value) {
      setAriaSearchAlertMessage('');
    } else if (filteredAccountsList) {
      if (filteredAccountsList.length === 0) {
        setAriaSearchAlertMessage('No matches found.');
      } else {
        setAriaSearchAlertMessage(
          `${filteredAccountsList.length} match${
            filteredAccountsList.length === 1 ? '' : 'es'
          } found`
        );
      }
    }
  };

  return (
    <>
      {accounts && accounts?.length > 10 && (
        <div className="p-24 sm:rounded-md bg-gray-lighter pii">
          <SearchAccounts formRef={formRef} handleChange={handleChange} />
        </div>
      )}
      <div className="mx-16">
        <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
          {ariaSearchAlertMessage}
        </div>
        <AccountSelector.Container>
          {accountList
            // toggles between showing all accounts or only the first 5
            ?.filter((_item, index) =>
              !showAll && accountList.length > 10 ? index < 5 : index >= 0
            )
            ?.map((account, index) => (
              <AccountSelector.Item
                forwardRef={
                  index === 0
                    ? firstAccountRef
                    : undefined || index === 5
                    ? sixthAccountRef
                    : undefined
                }
                onClick={() => {
                  selectAccount(account.AccountNum, account.Jurisdiction, selectAccountEndpoint)
                    .then(
                      (response: { Status: string; MessageText: string; ReturnCode: number }) => {
                        if (response.Status === 'Success') {
                          if (accounts) {
                            sendVpv('load', evaluateNumberOfAccounts, 'account-selected');
                            track({
                              event: 'cc-web-service',
                              alternate_id: account.AlternateAccountID,
                            });
                          }
                          window.location.href = selectedAccount.fields['Dashboard URL'].value.href;
                        } else {
                          throw new Error();
                        }
                      }
                    )
                    .catch((e: any) => {
                      const [, returnCode] = e.message.split(',');
                      if (!returnCodes) {
                        throw new Error('Unexpected error: no return codes');
                      }

                      const message = getMessageByName(returnCodes, returnCode);
                      sendVpv('error', evaluateNumberOfAccounts, returnCode);
                      sendCalNoReturnCode(
                        'account-selector_error',
                        `${returnCode}-|-${message}-|-${selectAccountEndpoint}`,
                        'list-of-accounts'
                      );
                    });
                }}
                key={index}
              >
                <div className="flex flex-wrap" data-testid="account-address">
                  <div className="mr-64">
                    <span aria-label={getAccountAriaLabel(account)} id={`account-${index}`}>
                      <strong className="pii">
                        {account.AccountNickname || account.CustomerNamePrimary}{' '}
                      </strong>
                      <strong>({getAccountNumber(account)})</strong>
                    </span>
                    {addressMasking ? (
                      <div className="capitalize pii">{`${getMaskedAddress(account)}...${
                        account.Unit
                      }`}</div>
                    ) : (
                      <DisplayAddress
                        apt={account.Unit}
                        houseNum={account.Housenum}
                        streetName={account.Street}
                        city={account.City}
                        state={account.State}
                        zipCode={account.Zipcode}
                      />
                    )}
                  </div>
                  {(account.Prepaid || account.Status) && (
                    <AccountChip
                      accountStatus={account.Status}
                      prepaid={account.Prepaid}
                      prepaidLabel={prepaidLabel}
                    />
                  )}
                </div>
              </AccountSelector.Item>
            ))}
        </AccountSelector.Container>

        {/* if a profile has more than 10 accounts the show all button will be displayed */}
        {accountList && accountList.length > 10 && (
          <ShowAllButton
            numberOfAccounts={accountList.length}
            showAll={showAll}
            toggleShowAll={handleToggleShowAll}
          />
        )}
      </div>
    </>
  );
};
